import { useState } from 'react';
import './FieldRenderer.css';

export default function FieldRenderer({ field, value, onChange, isNa, hasNote, isFlagged, onMarkNa, onRemoveNa, onAddNote, onFlag, onRemoveFlag, readOnly }) {
  const [showOverflow, setShowOverflow] = useState(false);
  const [noteMode, setNoteMode] = useState(false);
  const [noteText, setNoteText] = useState(hasNote ? (hasNote === true ? '' : hasNote) : '');

  function handleChange(newValue) {
    if (readOnly || isNa) return;
    onChange(field.uid, newValue);
  }

  function handleCheckboxChange(option, checked) {
    const current = Array.isArray(value) ? [...value] : [];
    if (checked) {
      if (!current.includes(option)) current.push(option);
    } else {
      const idx = current.indexOf(option);
      if (idx >= 0) current.splice(idx, 1);
    }
    handleChange(current);
  }

  function handleNoteSubmit() {
    if (onAddNote) onAddNote(field.uid, noteText);
    setNoteMode(false);
    setShowOverflow(false);
  }

  function renderInput() {
    if (isNa) {
      return <span className="fr__na-badge">N/A</span>;
    }

    const baseProps = {
      disabled: readOnly,
      className: 'fr__input'
    };

    if (field.field_type === 'text' || field.field_type === 'phone' || field.field_type === 'email') {
      return (
        <input
          {...baseProps}
          type={field.field_type === 'email' ? 'email' : 'text'}
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.hint || ''}
        />
      );
    }

    if (field.field_type === 'number') {
      return (
        <input
          {...baseProps}
          type="number"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.hint || ''}
        />
      );
    }

    if (field.field_type === 'date') {
      return (
        <input
          {...baseProps}
          type="date"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
        />
      );
    }

    if (field.field_type === 'textarea') {
      return (
        <textarea
          {...baseProps}
          className="fr__textarea"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={field.hint || ''}
          rows={3}
        />
      );
    }

    if (field.field_type === 'dropdown') {
      return (
        <select
          {...baseProps}
          className="fr__select"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
        >
          <option value="">Select...</option>
          {(field.options || []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (field.field_type === 'radio') {
      return (
        <div className="fr__radio-group">
          {(field.options || []).map(opt => (
            <label key={opt} className={`fr__radio-option ${readOnly ? 'fr__radio-option--disabled' : ''}`}>
              <input
                type="radio"
                name={field.uid}
                value={opt}
                checked={value === opt}
                onChange={() => handleChange(opt)}
                disabled={readOnly}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    if (field.field_type === 'checkbox') {
      const currentValues = Array.isArray(value) ? value : [];
      return (
        <div className="fr__checkbox-group">
          {(field.options || []).map(opt => (
            <label key={opt} className={`fr__checkbox-option ${readOnly ? 'fr__checkbox-option--disabled' : ''}`}>
              <input
                type="checkbox"
                value={opt}
                checked={currentValues.includes(opt)}
                onChange={e => handleCheckboxChange(opt, e.target.checked)}
                disabled={readOnly}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    return <span className="fr__unsupported">Unsupported field type: {field.field_type}</span>;
  }

  return (
    <div className={`fr ${isFlagged ? 'fr--flagged' : ''} ${isNa ? 'fr--na' : ''}`}>
      <div className="fr__header">
        <label className="fr__label">
          {field.label}
          {field.required && <span className="fr__required">*</span>}
        </label>
        {!readOnly && (
          <div className="fr__overflow-wrap">
            <button
              className="fr__overflow-btn"
              onClick={() => { setShowOverflow(!showOverflow); setNoteMode(false); }}
              title="Field options"
            >
              ⋯
            </button>
            {showOverflow && (
              <div className="fr__overflow-menu">
                {isNa ? (
                  <button className="fr__overflow-item" onClick={() => { onRemoveNa && onRemoveNa(field.uid); setShowOverflow(false); }}>
                    ↩ Remove N/A
                  </button>
                ) : (
                  <button className="fr__overflow-item" onClick={() => { onMarkNa && onMarkNa(field.uid); setShowOverflow(false); }}>
                    ⊘ Mark as N/A
                  </button>
                )}
                <button className="fr__overflow-item" onClick={() => { setNoteMode(true); setShowOverflow(false); }}>
                  📝 Add a note
                </button>
                {isFlagged ? (
                  <button className="fr__overflow-item" onClick={() => { onRemoveFlag && onRemoveFlag(field.uid); setShowOverflow(false); }}>
                    🚩 Remove flag
                  </button>
                ) : (
                  <button className="fr__overflow-item" onClick={() => { onFlag && onFlag(field.uid); setShowOverflow(false); }}>
                    🚩 Flag for review
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {renderInput()}

      {field.hint && !isNa && (
        <p className="fr__hint">{field.hint}</p>
      )}

      {noteMode && (
        <div className="fr__note-area">
          <textarea
            className="fr__note-input"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Add a note about this field..."
            rows={2}
            autoFocus
          />
          <div className="fr__note-actions">
            <button className="btn--primary" onClick={handleNoteSubmit}>Save Note</button>
            <button className="btn--outline" onClick={() => setNoteMode(false)}>Cancel</button>
          </div>
        </div>
      )}

      {hasNote && !noteMode && typeof hasNote === 'string' && hasNote.length > 0 && (
        <p className="fr__note-display">📝 {hasNote}</p>
      )}
    </div>
  );
}
